// ════════════════════════════════════════════════════════════════════
// Lectura de códigos QR
//
// QUÉ FALLABA ANTES
//
//   1. La librería de respaldo se descargaba de internet EN EL
//      MOMENTO de escanear. Sin señal en obra, o con el CDN lento, la
//      descarga fallaba, saltaba el error y la cámara se cerraba: eso
//      era la pantalla en negro. Ahora viaja dentro de la aplicación.
//
//   2. Cada fotograma se reducía a 800 píxeles antes de analizarlo.
//      El QR de una factura peruana es pequeño y con eso quedaba
//      ilegible: la cámara se veía bien pero nunca leía nada. Ahora se
//      recorta el centro y se analiza con mucho más detalle.
//
//   3. Un solo fotograma fallido apagaba la cámara. Ahora se ignora y
//      se sigue con el siguiente.
// ════════════════════════════════════════════════════════════════════

import jsQR from 'jsqr';

export const hayDetectorNativo = () =>
  typeof window !== 'undefined' && 'BarcodeDetector' in window;

let detectorNativo = null;
let detectorDescartado = false;

async function obtenerDetector() {
  if (detectorNativo || detectorDescartado) return detectorNativo;
  try {
    const formatos = await window.BarcodeDetector.getSupportedFormats?.();
    if (formatos && !formatos.includes('qr_code')) {
      detectorDescartado = true;
      return null;
    }
    detectorNativo = new window.BarcodeDetector({ formats: ['qr_code'] });
  } catch {
    detectorDescartado = true;
  }
  return detectorNativo;
}

/**
 * Busca un QR en un lienzo.
 * @param {boolean} intentarInvertido probar tambien en negativo; cuesta
 *        el doble, asi que solo se hace de vez en cuando
 */
async function leerDeLienzo(lienzo, intentarInvertido = false) {
  if (hayDetectorNativo() && !detectorDescartado) {
    const d = await obtenerDetector();
    if (d) {
      try {
        const encontrados = await d.detect(lienzo);
        if (encontrados.length > 0) return encontrados[0].rawValue;
      } catch {
        detectorDescartado = true;
      }
    }
  }

  const ctx = lienzo.getContext('2d', { willReadFrequently: true });
  const datos = ctx.getImageData(0, 0, lienzo.width, lienzo.height);
  const r = jsQR(datos.data, datos.width, datos.height, {
    inversionAttempts: intentarInvertido ? 'attemptBoth' : 'dontInvert',
  });
  return r?.data || null;
}

/** Lee un QR de una foto ya tomada. */
export function leerDeArchivo(archivo) {
  return new Promise((resolver, rechazar) => {
    const url = URL.createObjectURL(archivo);
    const img = new Image();

    img.onload = async () => {
      URL.revokeObjectURL(url);
      try {
        // Varias escalas: un QR pequeño dentro de una foto grande se
        // pierde al reducir, y uno muy grande a veces necesita reducirse.
        for (const lado of [1600, 1000, 2400]) {
          const escala = Math.min(1, lado / Math.max(img.width, img.height));
          const l = document.createElement('canvas');
          l.width = Math.round(img.width * escala);
          l.height = Math.round(img.height * escala);
          l.getContext('2d', { willReadFrequently: true })
            .drawImage(img, 0, 0, l.width, l.height);

          const texto = await leerDeLienzo(l, true);
          if (texto) return resolver(texto);
        }
        resolver(null);
      } catch (err) {
        rechazar(err);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      rechazar(new Error('No se pudo abrir la imagen'));
    };
    img.src = url;
  });
}

/**
 * Escanea con la cámara.
 * @param {function} o.alEstado avisos mientras tanto, para la pantalla
 */
export function escanearConCamara(video, {
  alLeer, alFallar, alEstado, segundosMax = 45,
} = {}) {
  let activo = true;
  let flujo = null;
  let temporizador = null;
  let cuadro = 0;

  const detener = () => {
    activo = false;
    clearTimeout(temporizador);
    if (flujo) flujo.getTracks().forEach((t) => t.stop());
    flujo = null;
    if (video) video.srcObject = null;
  };

  const avisar = (t) => { try { alEstado?.(t); } catch { /* sin importancia */ } };

  (async () => {
    try {
      avisar('Abriendo la cámara…');

      // Resolución alta a propósito: el QR de una factura es pequeño y
      // con poca resolución no hay nada que leer. Se prueban tres
      // configuraciones porque no todos los teléfonos aceptan la primera.
      const intentos = [
        { facingMode: { exact: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        { facingMode: { ideal: 'environment' }, width: { ideal: 1280 } },
        true,
      ];

      let ultimoError = null;
      for (const restriccion of intentos) {
        try {
          flujo = await navigator.mediaDevices.getUserMedia({ video: restriccion, audio: false });
          break;
        } catch (err) {
          ultimoError = err;
          // Un permiso denegado no mejora reintentando con otra cámara
          if (err.name === 'NotAllowedError') throw err;
        }
      }
      if (!flujo) throw ultimoError || new Error('Sin cámara');
      if (!activo) return detener();

      video.srcObject = flujo;
      video.setAttribute('playsinline', 'true');   // iPhone no abre pantalla completa
      video.setAttribute('muted', 'true');
      video.muted = true;

      try {
        await video.play();
      } catch (err) {
        // En iPhone play() puede rechazar aunque el vídeo sí arranque
        if (video.readyState === 0) throw err;
      }

      // Esperar a que el vídeo tenga medidas reales. Analizar antes
      // devuelve fotogramas vacíos y parece que la cámara no lee.
      const listo = await new Promise((res) => {
        if (video.videoWidth > 0) return res(true);
        let vueltas = 0;
        const revisarMedidas = () => {
          if (!activo) return res(false);
          if (video.videoWidth > 0) return res(true);
          if (++vueltas > 60) return res(false);
          setTimeout(revisarMedidas, 100);
        };
        revisarMedidas();
      });

      if (!activo) return detener();
      if (!listo) {
        detener();
        return alFallar('La cámara no llegó a mostrar imagen. '
                      + 'Usa la foto del documento.');
      }

      avisar('Apunta al código QR del comprobante');

      const lienzo = document.createElement('canvas');
      const ctx = lienzo.getContext('2d', { willReadFrequently: true });

      temporizador = setTimeout(() => {
        if (!activo) return;
        detener();
        alFallar('No se encontró ningún código. Acércate más al QR, '
               + 'o usa la foto del documento.');
      }, segundosMax * 1000);

      const revisar = async () => {
        if (!activo) return;

        if (video.readyState >= 2 && video.videoWidth > 0) {
          cuadro++;

          // Se recorta el centro en lugar de reducir la imagen entera:
          // así el QR conserva su detalle, que es lo que se perdía.
          const lado = Math.round(Math.min(video.videoWidth, video.videoHeight) * 0.85);
          const cx = Math.round((video.videoWidth - lado) / 2);
          const cy = Math.round((video.videoHeight - lado) / 2);
          const destino = Math.min(lado, 1100);

          lienzo.width = destino;
          lienzo.height = destino;
          ctx.drawImage(video, cx, cy, lado, lado, 0, 0, destino, destino);

          try {
            // En negativo solo cada cuarto fotograma: cuesta el doble y
            // casi todos los QR son oscuros sobre fondo claro.
            const texto = await leerDeLienzo(lienzo, cuadro % 4 === 0);
            if (texto && activo) {
              detener();
              return alLeer(texto);
            }
          } catch {
            // Un fotograma fallido ya no cierra la cámara: se sigue con
            // el siguiente. Antes, cualquier fallo la apagaba.
          }
        }

        if (activo) requestAnimationFrame(revisar);
      };

      requestAnimationFrame(revisar);
    } catch (err) {
      detener();
      const mensajes = {
        NotAllowedError: 'No diste permiso para usar la cámara. Puedes concederlo desde '
                       + 'el candado de la barra de direcciones, o tomar una foto del comprobante.',
        NotFoundError: 'Este dispositivo no tiene cámara disponible.',
        NotReadableError: 'Otra aplicación está usando la cámara. Ciérrala e inténtalo otra vez.',
        OverconstrainedError: 'No se encontró una cámara compatible.',
      };
      alFallar(mensajes[err.name] || `No se pudo abrir la cámara: ${err.message || err.name}`);
    }
  })();

  return { detener };
}

export async function prepararFoto(archivo, { ladoMaximo = 1600, calidad = 0.72 } = {}) {
  const url = URL.createObjectURL(archivo);
  try {
    const img = await new Promise((res, rej) => {
      const i = new Image();
      i.onload = () => res(i);
      i.onerror = () => rej(new Error('No se pudo abrir la imagen'));
      i.src = url;
    });

    const escala = Math.min(1, ladoMaximo / Math.max(img.width, img.height));
    const lienzo = document.createElement('canvas');
    lienzo.width = Math.round(img.width * escala);
    lienzo.height = Math.round(img.height * escala);

    const ctx = lienzo.getContext('2d');
    // Fondo blanco: los PNG con transparencia salen negros en JPEG.
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, lienzo.width, lienzo.height);
    ctx.drawImage(img, 0, 0, lienzo.width, lienzo.height);

    const dataUrl = lienzo.toDataURL('image/jpeg', calidad);
    const base64 = dataUrl.split(',')[1];

    return {
      nombre: archivo.name || 'comprobante.jpg',
      tipoMime: 'image/jpeg',
      base64,
      bytes: Math.round((base64.length * 3) / 4),
      ancho: lienzo.width,
      alto: lienzo.height,
    };
  } finally {
    URL.revokeObjectURL(url);
  }
}
